/**
 * Domain enums shared between api, agents and web.
 * These mirror the Prisma enums exactly — keep both in sync.
 * UI labels live in `labels.ts` (Norwegian), identifiers stay English.
 */

export const USER_ROLES = ['ADMIN', 'MEMBER', 'VIEWER'] as const;
export type UserRole = (typeof USER_ROLES)[number];

// Nova modules. MARKETING is today's Nova; SALES and ASSETS are new modules that
// can be switched on per installation and granted per user.
export const MODULE_KEYS = ['MARKETING', 'SALES', 'ASSETS'] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

// A user's access level within a module. No access = no row (there is no NONE).
// Ordered weakest → strongest; compare with MODULE_ROLE_RANK.
export const MODULE_ROLES = ['VIEWER', 'MEMBER', 'ADMIN'] as const;
export type ModuleRole = (typeof MODULE_ROLES)[number];

/** Numeric rank so `requireModule` can check "at least MEMBER" etc. */
export const MODULE_ROLE_RANK: Record<ModuleRole, number> = {
  VIEWER: 1,
  MEMBER: 2,
  ADMIN: 3,
};

export const AREA_TYPES = ['PRODUCT', 'COMPANY_PROFILE', 'EMPLOYEE_PROFILE'] as const;
export type AreaType = (typeof AREA_TYPES)[number];

export const COMPANIES = ['SPAMA', 'PROKOM', 'BEGGE'] as const;
export type Company = (typeof COMPANIES)[number];

// OWNER = the responsible (ansvarlig, mirrored from area.ownerUserId).
// MEMBER = a co-worker: area access, but NOT followed up by reminders.
// REMINDER_LIST = explicitly on the reminder CC for the area's posts.
export const AREA_MEMBER_ROLES = ['OWNER', 'MEMBER', 'REMINDER_LIST'] as const;
export type AreaMemberRole = (typeof AREA_MEMBER_ROLES)[number];

export const PILLARS = ['PRAKSIS', 'FAG', 'PRODUKT', 'BRANSJE'] as const;
export type Pillar = (typeof PILLARS)[number];

export const CONTENT_FORMATS = [
  'ARTIKKEL',
  'KUNDECASE',
  'LINKEDIN_POST',
  'NYHETSBREV',
  'WEBINAR',
  'ARRANGEMENT',
  'VIDEO',
  'ANNET',
  // Trykk/fysisk — print and physical formats (redaksjonsmøte).
  'PRODUKTARK',
  'PLAGG',
  'MERCH',
  'ANNONSE',
] as const;
export type ContentFormat = (typeof CONTENT_FORMATS)[number];

/**
 * How the format picker is grouped: digital formats (produced and published in
 * Nova's own channels) vs. print/physical ones (prepared here, produced on
 * paper or cloth). Defined once so every picker groups them the same way.
 */
export const CONTENT_FORMAT_GROUPS = {
  DIGITALT: [
    'ARTIKKEL',
    'KUNDECASE',
    'LINKEDIN_POST',
    'NYHETSBREV',
    'WEBINAR',
    'VIDEO',
    'ARRANGEMENT',
    'ANNET',
  ],
  TRYKK_FYSISK: ['PRODUKTARK', 'PLAGG', 'MERCH', 'ANNONSE'],
} as const satisfies Record<string, readonly ContentFormat[]>;

export type ContentFormatGroup = keyof typeof CONTENT_FORMAT_GROUPS;

/**
 * Formats that happen on a given day (a webinar, an arrangement) and therefore
 * carry an «event date» — the date of execution, distinct from when we publish
 * about it. Used to show the date field and to catch «two events the same week».
 */
export const EVENT_FORMATS = ['WEBINAR', 'ARRANGEMENT'] as const satisfies readonly ContentFormat[];
export function isEventFormat(format: ContentFormat): boolean {
  return (EVENT_FORMATS as readonly ContentFormat[]).includes(format);
}

export const CONTENT_STATUSES = [
  // PROPOSED is a hidden pre-forslag: an agent-drafted idea that waits in the
  // review queue (the Tinder-wizard) until the area owner accepts it onto the
  // board (→ SUGGESTED) or discards it. It never shows on the kanban or lists.
  'PROPOSED',
  'SUGGESTED',
  'APPROVED',
  'DRAFT',
  'SCHEDULED',
  'PUBLISHED',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
] as const;
export type ContentStatus = (typeof CONTENT_STATUSES)[number];

/** Statuses that represent live, in-flight work (not archived/dead). */
export const ACTIVE_CONTENT_STATUSES = [
  'SUGGESTED',
  'APPROVED',
  'DRAFT',
  'SCHEDULED',
] as const satisfies readonly ContentStatus[];

/**
 * Statuses kept off the board and all active lists. The three retired ones
 * (avvist, kansellert, utgått) plus PROPOSED — a pre-forslag only ever seen in
 * the review wizard, never on the kanban. Mirrors the `activeOnly` filter and
 * the collision/board exclusions on the API.
 */
export const NON_BOARD_STATUSES = [
  'PROPOSED',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
] as const satisfies readonly ContentStatus[];

export const CONTENT_SOURCES = ['AGENT', 'HUMAN'] as const;
export type ContentSource = (typeof CONTENT_SOURCES)[number];

export const CHANNEL_TYPES = [
  'WEBSITE_AIMCMS',
  'LINKEDIN_ORG',
  'LINKEDIN_EMPLOYEE',
  'NEWSLETTER_MAKE',
  'WEBINAR',
  'TRYKK',
  'OTHER',
] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

export const PUBLISH_MODES = ['API', 'MANUAL_PREPARED'] as const;
export type PublishMode = (typeof PUBLISH_MODES)[number];

export const CHANNEL_TARGET_STATUSES = ['PENDING', 'PREPARED', 'PUBLISHED_CONFIRMED'] as const;
export type ChannelTargetStatus = (typeof CHANNEL_TARGET_STATUSES)[number];

export const AGENT_KEYS = [
  'director',
  'planner',
  'scout',
  'writer',
  'reminder',
  'analyst',
  'digest',
  'tender-scout',
] as const;
export type AgentKey = (typeof AGENT_KEYS)[number];

export const AGENT_RUN_TRIGGERS = ['SCHEDULE', 'MANUAL', 'DIRECTOR'] as const;
export type AgentRunTrigger = (typeof AGENT_RUN_TRIGGERS)[number];

export const AGENT_RUN_STATUSES = ['RUNNING', 'SUCCESS', 'FAILED', 'PARTIAL'] as const;
export type AgentRunStatus = (typeof AGENT_RUN_STATUSES)[number];

export const NOTIFICATION_TYPES = [
  'REMINDER',
  'ESCALATION',
  'INFO',
  'PREPARED_POST',
  'COMMENT',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const NOTIFICATION_CHANNELS = ['TEAMS', 'EMAIL', 'INAPP'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const KPI_SOURCES = ['MANUAL', 'MAKE', 'LINKEDIN', 'INTERNAL'] as const;
export type KpiSource = (typeof KPI_SOURCES)[number];

export const ACTOR_TYPES = ['USER', 'AGENT', 'SYSTEM'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

// ── SoMe-overvåkning (Marked) ────────────────────────────────────────────────
// Register over selskapenes sosiale kontoer og månedlige tall. Mirror the
// Prisma enums in apps/api/prisma/schema.prisma exactly.

/** Hvilken plattform kontoen ligger på. Facebook er overvåkning-bare i v1. */
export const SOCIAL_PLATFORMS = ['LINKEDIN', 'FACEBOOK'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/** Hva slags konto: selskapsside, ansattprofil, Facebook-side eller annet. */
export const SOCIAL_ACCOUNT_KINDS = ['COMPANY', 'EMPLOYEE', 'PAGE', 'OTHER'] as const;
export type SocialAccountKind = (typeof SOCIAL_ACCOUNT_KINDS)[number];

/**
 * Hvor en måling kom fra. MANUAL er dagens kilde; CHROME (nettleserleser) og
 * INTERNAL (integrasjoner) er lagt inn for senere faser — feltet holder rom åpent.
 */
export const SOCIAL_METRIC_SOURCES = ['MANUAL', 'CHROME', 'INTERNAL'] as const;
export type SocialMetricSource = (typeof SOCIAL_METRIC_SOURCES)[number];

/**
 * Helseflagget for en konto i overvåkningen — avledet, ikke lagret.
 * Beregnes av den rene funksjonen `socialHealth` (social-health.ts).
 */
export const SOCIAL_HEALTH_FLAGS = ['VOKSER', 'FLAT', 'STILLE', 'INAKTIV'] as const;
export type SocialHealthFlag = (typeof SOCIAL_HEALTH_FLAGS)[number];

export const SIGNAL_STATUSES = ['NEW', 'USED', 'DISMISSED'] as const;
export type SignalStatus = (typeof SIGNAL_STATUSES)[number];

export const DISCOVERED_SOURCE_STATUSES = ['SUGGESTED', 'PROMOTED', 'DISMISSED'] as const;
export type DiscoveredSourceStatus = (typeof DISCOVERED_SOURCE_STATUSES)[number];

export const BRAND_SCOPES = ['GLOBAL', 'COMPANY', 'AREA', 'CHANNEL'] as const;
export type BrandScope = (typeof BRAND_SCOPES)[number];

export const PROFILE_DOCUMENT_TYPES = [
  'ENDRINGSLOGG',
  'VEIKART',
  'PRODUKTARK',
  'MERKEVAREMANUAL',
  'ANNET',
] as const;
export type ProfileDocumentType = (typeof PROFILE_DOCUMENT_TYPES)[number];

export const PROFILE_DOCUMENT_KINDS = ['LINK', 'FILE', 'INLINE'] as const;
export type ProfileDocumentKind = (typeof PROFILE_DOCUMENT_KINDS)[number];

// ── Salg / Anbudsradar (SALES-modulen) ───────────────────────────────────────
// Domenenums for anbudsradaren. Speiler Prisma-enumene i schema.prisma.

/** Kunngjøringstype fra Doffin. Tildeling tas med for markedsoversikten (hvem vant). */
export const TENDER_NOTICE_TYPES = ['KONKURRANSE', 'FORHANDSKUNNGJORING', 'TILDELING'] as const;
export type TenderNoticeType = (typeof TENDER_NOTICE_TYPES)[number];

/**
 * Statusflyten for et anbud:
 * NY → VURDERES → BID | NO_BID → UNDER_ARBEID → LEVERT → VUNNET | TAPT | AVLYST.
 * NO_BID krever alltid en årsak fra TENDER_NO_BID_REASONS (læringsdata).
 */
export const TENDER_STATUSES = [
  'NY',
  'VURDERES',
  'BID',
  'NO_BID',
  'UNDER_ARBEID',
  'LEVERT',
  'VUNNET',
  'TAPT',
  'AVLYST',
] as const;
export type TenderStatus = (typeof TENDER_STATUSES)[number];

/** Fast liste med no-bid-årsaker — læringsdata, ikke fritekst (02-anbudsradar-doffin.md). */
export const TENDER_NO_BID_REASONS = [
  'KAPASITET',
  'KRAV_IKKE_OPPFYLT',
  'PRIS',
  'FEIL_PRODUKT',
  'STRATEGISK',
  'ANNET',
] as const;
export type TenderNoBidReason = (typeof TENDER_NO_BID_REASONS)[number];

/** Oppdragsgivertyper en overvåkningsprofil kan avgrense til. */
export const TENDER_BUYER_TYPES = ['KOMMUNE', 'FYLKESKOMMUNE', 'STAT', 'HELSE', 'ANNET'] as const;
export type TenderBuyerType = (typeof TENDER_BUYER_TYPES)[number];

/** Utfallet av en tender-scout-kjøring, lagret på TenderCrawlRun. */
export const TENDER_CRAWL_STATUSES = ['SUCCESS', 'PARTIAL', 'FAILED', 'SKIPPED'] as const;
export type TenderCrawlStatus = (typeof TENDER_CRAWL_STATUSES)[number];
// ── Utstyr og lisenser (ASSETS module) ───────────────────────────────────────
// Mirror the Prisma enums in apps/api/prisma/schema.prisma exactly.

export const SOFTWARE_CATEGORIES = [
  'PRODUCTIVITY',
  'AI',
  'DESIGN',
  'DEV',
  'COMMUNICATION',
  'FINANCE',
  'OTHER',
] as const;
export type SoftwareCategory = (typeof SOFTWARE_CATEGORIES)[number];

export const BILLING_PERIODS = ['MONTHLY', 'YEARLY'] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

export const REQUIREMENT_SCOPES = ['ALL_EMPLOYEES', 'GROUP'] as const;
export type RequirementScope = (typeof REQUIREMENT_SCOPES)[number];

export const ASSIGNMENT_SOURCES = ['MANUAL', 'IMPORT', 'GRAPH', 'SELF_REPORTED'] as const;
export type AssignmentSource = (typeof ASSIGNMENT_SOURCES)[number];

export const USE_STATUSES = ['IN_USE', 'NOT_IN_USE', 'ENDED'] as const;
export type UseStatus = (typeof USE_STATUSES)[number];

/** Licence statuses that still cost money (until a licence is ENDED). */
export const COSTED_USE_STATUSES = ['IN_USE', 'NOT_IN_USE'] as const satisfies readonly UseStatus[];

export const EQUIPMENT_CATEGORIES = [
  'PC',
  'MOBILE',
  'TABLET',
  'MONITOR',
  'HEADSET',
  'DOCK',
  'KEYBOARD_MOUSE',
  'OTHER',
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number];

export const EQUIPMENT_STATUSES = ['IN_USE', 'NOT_IN_USE', 'RETURNED', 'LOST', 'BROKEN'] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number];

/** Equipment statuses that still count as a bought, held asset (a cost). */
export const COSTED_EQUIPMENT_STATUSES = [
  'IN_USE',
  'NOT_IN_USE',
] as const satisfies readonly EquipmentStatus[];

export const REPORT_STATUSES = ['OPEN', 'WITHDRAWN', 'RESOLVED'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export const NEED_TYPES = ['SOFTWARE', 'EQUIPMENT', 'OTHER'] as const;
export type NeedType = (typeof NEED_TYPES)[number];

export const NEED_STATUSES = [
  'NEW',
  'IN_REVIEW',
  'APPROVED',
  'REJECTED',
  'ORDERED',
  'DONE',
] as const;
export type NeedStatus = (typeof NEED_STATUSES)[number];

export const ASSET_IMPORT_ENTITIES = ['LICENCE', 'EQUIPMENT'] as const;
export type AssetImportEntity = (typeof ASSET_IMPORT_ENTITIES)[number];

// ── Salg / Marked og kunder (SALES-modulen, fase S2) ─────────────────────────
// Mirror the Prisma enums in apps/api/prisma/schema.prisma exactly.

/** Organisasjonstype. Modellen er ikke kommune-spesifikk (03-marked-og-kunder.md). */
export const ORGANIZATION_TYPES = [
  'KOMMUNE',
  'FYLKESKOMMUNE',
  'STAT',
  'HELSE',
  'BANK',
  'PRIVAT',
  'ANNET',
] as const;
export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

/** Status for en organisasjon × produkt — kjernen i white space-kartet. */
export const PRODUCT_HOLDING_STATUSES = [
  'KUNDE',
  'TIDLIGERE_KUNDE',
  'PROSPEKT',
  'KONKURRENT_KUNDE',
  'IKKE_RELEVANT',
] as const;
export type ProductHoldingStatus = (typeof PRODUCT_HOLDING_STATUSES)[number];

/**
 * Precedence for the dominant status that colours a kommune when it holds several
 * matching products. Earlier wins: our strongest active relationship first
 * (KUNDE), then active prospecting, then known competitor accounts, then the rest.
 * The map tooltip still lists every holding — this only drives the fill colour.
 */
export const MAP_DOMINANT_STATUS_ORDER: readonly ProductHoldingStatus[] = [
  'KUNDE',
  'PROSPEKT',
  'KONKURRENT_KUNDE',
  'TIDLIGERE_KUNDE',
  'IKKE_RELEVANT',
] as const;

/** Datakvalitet på en beholdningsopplysning — så «indikasjon» ikke ser ut som fakta. */
export const HOLDING_CONFIDENCES = ['HOY', 'MIDDELS', 'LAV'] as const;
export type HoldingConfidence = (typeof HOLDING_CONFIDENCES)[number];

/** Type oppfølging (CRM-lett). */
export const INTERACTION_TYPES = ['MOTE', 'SAMTALE', 'EPOST', 'DEMO', 'TILBUD', 'ANNET'] as const;
export type InteractionType = (typeof INTERACTION_TYPES)[number];

// ── Salg / Prioritering + situasjonskartlegging (SALES-modulen, fase S3) ──────
// Mirror the Prisma enums in apps/api/prisma/schema.prisma exactly.

/** Manuell prioritetstier — settes av et menneske og vinner alltid over scoren. */
export const PRIORITY_TIERS = ['A', 'B', 'C'] as const;
export type PriorityTier = (typeof PRIORITY_TIERS)[number];

/** Faktorene i den beregnede prioritetsscoren (vektene redigeres per produkt). */
export const PRIORITY_FACTOR_KEYS = [
  'contractExpiry',
  'size',
  'fit',
  'relationship',
  'signals',
] as const;
export type PriorityFactorKey = (typeof PRIORITY_FACTOR_KEYS)[number];

/** Status på en situasjonskartlegging: agentvurdering til et menneske bekrefter. */
export const ASSESSMENT_STATUSES = ['AGENT', 'CONFIRMED'] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];
